import React, { useState } from 'react'
import { Box, IconButton, Tooltip, alpha } from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import SectionCard from '../components/SectionCard'
import { advancedColors } from '../theme'

export type PanelChrome = 'card' | 'bare'

type Props = {
  title: string
  chrome: PanelChrome
  dragHandle?: React.ReactNode
  menu?: React.ReactNode
  // Shown read-only too, unlike `dragHandle`/`menu`; the collapsed state is per mount, never persisted.
  collapsible?: boolean
  tourAnchor?: string
  children: React.ReactNode
}

// Stretching to the row height keeps a row of stat tiles level when only some carry a caption.
const wrapperSx = {
  position: 'relative' as const,
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  '& > *': { flexGrow: 1, minWidth: 0 },
}

// Dimmed rather than hidden: the menu renders in a portal, so hiding on blur takes its own anchor with it.
const bareMenuSx = {
  position: 'absolute' as const,
  top: 6,
  right: 6,
  zIndex: 1,
  display: 'flex',
  borderRadius: 999,
  backgroundColor: alpha(advancedColors.surface, 0.92),
  border: `1px solid ${advancedColors.line}`,
  opacity: 0.4,
  transition: 'opacity 120ms ease-in-out',
}

const PanelFrame = ({ title, chrome, dragHandle, menu, collapsible, tourAnchor, children }: Props) => {
  const [collapsed, setCollapsed] = useState(false)

  const collapseToggle = collapsible && (
    <Tooltip title={collapsed ? `Expand — ${title}` : `Collapse — ${title}`}>
      <IconButton
        size="small"
        aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
        onClick={() => setCollapsed((prev) => !prev)}
        sx={{ color: advancedColors.inkMuted }}
      >
        <ExpandMoreRoundedIcon fontSize="small" sx={{ transform: collapsed ? 'none' : 'rotate(180deg)' }} />
      </IconButton>
    </Tooltip>
  )

  const controls = (dragHandle || menu || collapseToggle) && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      {dragHandle}
      {menu}
      {collapseToggle}
    </Box>
  )

  if (chrome === 'card') {
    return (
      <Box data-tour={tourAnchor} sx={wrapperSx}>
        <SectionCard title={title} action={controls} bodyHidden={collapsible && collapsed}>
          {children}
        </SectionCard>
      </Box>
    )
  }

  return (
    <Box
      data-tour={tourAnchor}
      sx={{
        ...wrapperSx,
        '&:hover .AdvancedPanelFrame-menu, &:focus-within .AdvancedPanelFrame-menu': { opacity: 1 },
      }}
    >
      {children}
      {controls && (
        <Box className="AdvancedPanelFrame-menu" sx={bareMenuSx}>
          {controls}
        </Box>
      )}
    </Box>
  )
}

export default PanelFrame
